import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import url from '../constants/config';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

/* Status to use when dropping into a column */
const COLUMN_DROP_STATUS = {
  todo: 'not_started',
  in_progress: 'in_progress',
  waiting: 'waiting',
  to_verify: 'to_verify',
  done: 'done',
};

const COLUMNS = [
  {
    key: 'todo',
    label: 'Do zrobienia',
    statuses: ['not_started'],
    color: '#475569',
    bg: '#f8fafc',
    border: '#e2e8f0',
    countBg: '#64748b',
    dropBg: '#e2e8f0',
  },
  {
    key: 'in_progress',
    label: 'W trakcie',
    statuses: ['in_progress'],
    color: '#1d4ed8',
    bg: '#eff6ff',
    border: '#bfdbfe',
    countBg: '#1d4ed8',
    dropBg: '#bfdbfe',
  },
  {
    key: 'waiting',
    label: 'Oczekuje',
    statuses: ['waiting', 'waiting_athlete', 'waiting_university', 'waiting_playsport', 'on_hold'],
    color: '#b45309',
    bg: '#fffbeb',
    border: '#fde68a',
    countBg: '#d97706',
    dropBg: '#fde68a',
  },
  {
    key: 'to_verify',
    label: 'Do weryfikacji',
    statuses: ['to_verify'],
    color: '#059669',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    countBg: '#059669',
    dropBg: '#bbf7d0',
  },
  {
    key: 'done',
    label: 'Gotowe',
    statuses: ['done'],
    color: '#15803d',
    bg: '#dcfce7',
    border: '#86efac',
    countBg: '#15803d',
    dropBg: '#86efac',
  },
];

const STATUS_SUBLABELS = {
  waiting: 'Oczekuje',
  waiting_athlete: 'Czeka sportowiec',
  waiting_university: 'Czeka uczelnia',
  waiting_playsport: 'Czeka PLAYSPORT',
  on_hold: 'Wstrzymane',
};

const DONE_DEFAULT_LIMIT = 20;

const daysSince = updatedAt => {
  if (!updatedAt) return null;
  return Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000);
};

const DaysChip = ({ days }) => {
  if (days === null) return null;
  const color = days >= 7 ? '#ef4444' : days >= 3 ? '#f59e0b' : '#94a3b8';
  return (
    <span style={{ fontSize: '10px', color, fontWeight: 600 }}>
      {days === 0 ? 'dziś' : `${days}d`}
    </span>
  );
};

const AssistantDashboard = () => {
  const history = useHistory();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllDone, setShowAllDone] = useState(false);
  const [filterPlayer, setFilterPlayer] = useState('');

  /* Drag state */
  const [draggingId, setDraggingId] = useState(null);   // task._id being dragged
  const [overColumnKey, setOverColumnKey] = useState(null); // column being hovered
  const dragRef = useRef(null); // { taskId, fromColumnKey }

  useEffect(() => {
    axios
      .get(`${url}/checklist/my-tasks`, { headers: getAuthHeader() })
      .then(res => setTasks(res.data.tasks || []))
      .catch(() => setError('Nie udało się załadować zadań.'))
      .finally(() => setLoading(false));
  }, []);

  /* Separate player tasks from global tasks */
  const globalTasks = tasks.filter(t => t.isGlobal);
  const playerTasks = tasks.filter(t => !t.isGlobal);

  const players = useMemo(() => {
    const map = {};
    playerTasks.forEach(t => { if (t.playerId) map[t.playerId] = t.playerName; });
    return Object.entries(map)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [playerTasks]);

  const filteredTasks = useMemo(() => {
    // Include both global tasks and player tasks in kanban
    const allTasks = [...globalTasks, ...playerTasks];
    // Filter by player if selected (but always include global tasks)
    return filterPlayer
      ? allTasks.filter(t => !t.playerId || t.playerId === filterPlayer)
      : allTasks;
  }, [globalTasks, playerTasks, filterPlayer]);

  const columns = useMemo(() => {
    return COLUMNS.map(col => {
      let colTasks = filteredTasks
        .filter(t => col.statuses.includes(t.status))
        .sort((a, b) => {
          // Global tasks always on top
          if (a.isGlobal && !b.isGlobal) return -1;
          if (!a.isGlobal && b.isGlobal) return 1;
          // Then sort by days since update (oldest first)
          return (daysSince(b.updatedAt) ?? 0) - (daysSince(a.updatedAt) ?? 0);
        });
      const total = colTasks.length;
      if (col.key === 'done' && !showAllDone) {
        colTasks = colTasks.slice(0, DONE_DEFAULT_LIMIT);
      }
      return { ...col, tasks: colTasks, total };
    });
  }, [filteredTasks, showAllDone]);

  const totalActive = filteredTasks.filter(t => t.status !== 'done').length;
  const totalDone = filteredTasks.filter(t => t.status === 'done').length;

  /* ── Drag handlers ── */
  const handleDragStart = useCallback((e, task, fromColumnKey) => {
    dragRef.current = { taskId: task._id, playerId: task.playerId, fromColumnKey };
    setDraggingId(task._id);
    // Store data for cross-window safety (not needed here but good practice)
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task._id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setOverColumnKey(null);
    dragRef.current = null;
  }, []);

  const handleDragOver = useCallback((e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverColumnKey(columnKey);
  }, []);

  const handleDragLeave = useCallback((e) => {
    // Only clear if we're leaving the column entirely (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setOverColumnKey(null);
    }
  }, []);

  const handleDrop = useCallback(async (e, targetColumnKey) => {
    e.preventDefault();
    setOverColumnKey(null);
    setDraggingId(null);

    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;

    if (drag.fromColumnKey === targetColumnKey) return; // same column — no-op

    const newStatus = COLUMN_DROP_STATUS[targetColumnKey];
    if (!newStatus) return;

    const { taskId, playerId } = drag;

    // Optimistic update
    setTasks(prev =>
      prev.map(t =>
        t._id === taskId
          ? { ...t, status: newStatus, updatedAt: new Date().toISOString() }
          : t,
      ),
    );

    try {
      // Handle both global tasks and player tasks
      const endpoint = playerId
        ? `${url}/checklist/${playerId}/tasks/${taskId}`
        : `${url}/checklist/global-tasks/${taskId}`;

      await axios.patch(
        endpoint,
        { status: newStatus },
        { headers: getAuthHeader() },
      );
    } catch {
      // Rollback on failure
      setTasks(prev =>
        prev.map(t => (t._id === taskId ? { ...t, status: COLUMNS.find(c => c.key === drag.fromColumnKey)?.statuses[0] || t.status } : t)),
      );
    }
  }, []);

  if (loading) return <Loader />;
  if (error) return <div style={{ padding: '32px', color: '#ef4444' }}>{error}</div>;

  return (
    <div style={{ padding: '24px 32px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
            <i className="fas fa-columns" style={{ marginRight: '10px', color: '#2e496b' }} />
            Moje zadania
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
            {totalActive > 0 && <><strong>{totalActive}</strong> aktywnych · </>}
            <strong>{totalDone}</strong> ukończonych
            <span style={{ marginLeft: '10px', color: '#94a3b8', fontSize: '12px' }}>
              — przeciągnij kartę by zmienić status
            </span>
          </p>
        </div>

        {players.length > 1 && (
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
              Filtruj po zawodniku
            </label>
            <select
              value={filterPlayer}
              onChange={e => setFilterPlayer(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '13px',
                minWidth: '180px',
                background: '#fff',
              }}
            >
              <option value="">Wszyscy zawodnicy</option>
              {players.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>


      {/* Empty state */}
      {playerTasks.length === 0 && globalTasks.length === 0 && (
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '56px',
            textAlign: 'center',
          }}
        >
          <i className="fas fa-clipboard-list" style={{ fontSize: '36px', color: '#94a3b8', marginBottom: '14px', display: 'block' }} />
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Nie masz jeszcze przydzielonych zadań.
          </p>
        </div>
      )}

      {/* Kanban board */}
      {playerTasks.length > 0 && (
        <div style={{ overflowX: 'auto', paddingBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              gap: '14px',
              minWidth: `${COLUMNS.length * 194}px`,
              alignItems: 'flex-start',
            }}
          >
            {columns.map(col => {
              const isOver = overColumnKey === col.key;
              return (
                <div
                  key={col.key}
                  style={{ flex: '0 0 222px', width: '222px' }}
                >
                  {/* Column header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      background: isOver ? col.dropBg : col.bg,
                      border: `1px solid ${isOver ? col.countBg : col.border}`,
                      borderRadius: '8px 8px 0 0',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: '13px', color: col.color }}>
                      {col.label}
                    </span>
                    <span
                      style={{
                        background: col.countBg,
                        color: '#fff',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        minWidth: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {col.total}
                    </span>
                  </div>

                  {/* Drop zone */}
                  <div
                    onDragOver={e => handleDragOver(e, col.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => handleDrop(e, col.key)}
                    style={{
                      background: isOver ? col.dropBg : '#f1f5f9',
                      border: `1px solid ${isOver ? col.countBg : col.border}`,
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      minHeight: '200px',
                      padding: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      transition: 'background 0.15s, border-color 0.15s',
                      outline: isOver ? `2px dashed ${col.countBg}` : 'none',
                      outlineOffset: '-4px',
                    }}
                  >
                    {/* Drop hint when hovering with a card */}
                    {isOver && draggingId && (
                      <div
                        style={{
                          border: `2px dashed ${col.countBg}`,
                          borderRadius: '8px',
                          padding: '12px',
                          textAlign: 'center',
                          color: col.color,
                          fontSize: '12px',
                          fontWeight: 600,
                          background: col.bg,
                          marginBottom: '4px',
                          opacity: 0.85,
                        }}
                      >
                        Upuść tutaj → {col.label}
                      </div>
                    )}

                    {col.tasks.map(task => {
                      const days = daysSince(task.updatedAt);
                      const subLabel = STATUS_SUBLABELS[task.status];
                      const isDragging = draggingId === task._id;

                      return (
                        <div
                          key={task._id}
                          draggable
                          onDragStart={e => handleDragStart(e, task, col.key)}
                          onDragEnd={handleDragEnd}
                          style={{
                            background: task.isGlobal ? '#fffbeb' : '#fff',
                            border: '1px solid #e2e8f0',
                            borderLeft: task.isGlobal ? '3px solid #f59e0b' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            cursor: 'grab',
                            boxShadow: isDragging
                              ? '0 8px 24px rgba(0,0,0,0.18)'
                              : '0 1px 2px rgba(0,0,0,0.05)',
                            opacity: isDragging ? 0.5 : 1,
                            transform: isDragging ? 'rotate(2deg)' : 'none',
                            transition: 'box-shadow 0.15s, opacity 0.15s, transform 0.15s',
                            userSelect: 'none',
                            position: 'relative',
                          }}
                        >
                          {/* Drag handle indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '6px',
                              right: '8px',
                              color: '#cbd5e1',
                              fontSize: '11px',
                              letterSpacing: '1px',
                              lineHeight: '1',
                            }}
                          >
                            ⠿
                          </div>

                          {/* Player name or global indicator */}
                          <div
                            style={{
                              fontSize: '10px',
                              color: task.isGlobal ? '#f59e0b' : '#2e496b',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '4px',
                              paddingRight: '16px',
                            }}
                          >
                            {task.isGlobal ? '⭐ Ogólnie' : task.playerName}
                          </div>

                          {/* Task label */}
                          <div
                            style={{
                              fontSize: '13px',
                              color: '#1e293b',
                              lineHeight: '1.4',
                              marginBottom: '6px',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {task.label}
                          </div>

                          {/* Footer */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '4px',
                            }}
                          >
                            {subLabel && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  color: '#92400e',
                                  background: '#fef3c7',
                                  borderRadius: '4px',
                                  padding: '1px 5px',
                                }}
                              >
                                {subLabel}
                              </span>
                            )}
                            <span style={{ marginLeft: 'auto' }}>
                              <DaysChip days={days} />
                            </span>
                          </div>

                          {task.deadline && (
                            <div style={{ fontSize: '10px', color: '#6366f1', marginTop: '4px' }}>
                              <span role="img" aria-label="termin">⏰</span> {new Date(task.deadline).toLocaleDateString('pl-PL')}
                            </div>
                          )}

                          {/* Click to open checklist (only for player tasks) */}
                          {!task.isGlobal && (
                            <button
                              type="button"
                              onClick={() =>
                                history.push(`/admin/my-players/${task.playerId}?tab=checklist`)
                              }
                              style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '8px',
                              }}
                              tabIndex={-1}
                              aria-label={`Otwórz zadanie: ${task.label}`}
                            />
                          )}
                        </div>
                      );
                    })}

                    {col.tasks.length === 0 && !isOver && (
                      <div
                        style={{
                          padding: '24px 12px',
                          textAlign: 'center',
                          color: '#cbd5e1',
                          fontSize: '12px',
                        }}
                      >
                        Brak zadań
                      </div>
                    )}

                    {col.key === 'done' && col.total > DONE_DEFAULT_LIMIT && !showAllDone && (
                      <button
                        type="button"
                        onClick={() => setShowAllDone(true)}
                        style={{
                          background: 'none',
                          border: `1px dashed ${col.border}`,
                          borderRadius: '6px',
                          padding: '7px',
                          color: col.color,
                          fontSize: '12px',
                          cursor: 'pointer',
                          width: '100%',
                          fontWeight: 600,
                        }}
                      >
                        + Pokaż wszystkie ({col.total})
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantDashboard;
